<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'

const props = defineProps<{
  modelValue: string
  length?: number
  disabled?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'complete', value: string): void
}>()

const length = props.length || 6
const digits = ref<string[]>(new Array(length).fill(''))
const inputRefs = ref<HTMLInputElement[]>([])

// Initialize from modelValue
watch(() => props.modelValue, (newVal) => {
  if (newVal.length === length && newVal !== digits.value.join('')) {
    digits.value = newVal.split('')
  } else if (newVal === '') {
    digits.value = new Array(length).fill('')
  }
}, { immediate: true })

function onInput(index: number, event: Event) {
  const input = event.target as HTMLInputElement
  const value = input.value.slice(-1) // Only take the last character

  if (!/^\d$/.test(value)) {
    digits.value[index] = ''
    return
  }

  digits.value[index] = value
  emitValue()

  if (value && index < length - 1) {
    inputRefs.value[index + 1]?.focus()
  }
}

function onKeyDown(index: number, event: KeyboardEvent) {
  if (event.key === 'Backspace') {
    if (!digits.value[index] && index > 0) {
      inputRefs.value[index - 1]?.focus()
    }
    digits.value[index] = ''
    emitValue()
  }
}

function onPaste(event: ClipboardEvent) {
  const pasteData = event.clipboardData?.getData('text')
  if (pasteData && new RegExp(`^\\d{${length}}$`).test(pasteData)) {
    digits.value = pasteData.split('')
    emitValue()
    inputRefs.value[length - 1]?.focus()
    emit('complete', pasteData)
  }
}

const lastEmittedComplete = ref('')

function emitValue() {
  const val = digits.value.join('')
  emit('update:modelValue', val)
  if (val.length === length && val !== lastEmittedComplete.value) {
    lastEmittedComplete.value = val
    emit('complete', val)
  } else if (val.length < length) {
    lastEmittedComplete.value = ''
  }
}

onMounted(() => {
    // Optional: auto-focus first input
    // inputRefs.value[0]?.focus()
})

defineExpose({
    focus: () => inputRefs.value[0]?.focus(),
    clear: () => {
        digits.value = new Array(length).fill('')
        emitValue()
    }
})
</script>

<template>
  <div class="flex justify-between gap-2 w-full max-w-[320px] mx-auto">
    <input
      v-for="(_, idx) in length"
      :key="idx"
      ref="inputRefs"
      v-model="digits[idx]"
      type="text"
      inputmode="numeric"
      autocomplete="one-time-code"
      pattern="\d*"
      maxlength="1"
      :disabled="disabled"
      class="w-full aspect-[3/4] text-center text-2xl font-bold rounded-xl border-2 border-gray-200 dark:border-gray-800 bg-transparent focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all outline-none disabled:opacity-50"
      @input="onInput(idx, $event)"
      @keydown="onKeyDown(idx, $event)"
      @paste="onPaste"
    />
  </div>
</template>
